import React, { useState } from 'react';
import styled from 'styled-components';
import yaml from 'js-yaml';

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
`;

const Modal = styled.div`
  background: white;
  border-radius: 8px;
  width: 500px;
  max-width: 90%;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
`;

const Header = styled.div`
  padding: 20px;
  border-bottom: 1px solid #e1e4e8;
`;

const Title = styled.h2`
  margin: 0;
  font-size: 18px;
  color: #24292f;
`;

const Content = styled.div`
  padding: 20px;
`;

const TabContainer = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 20px;
  border-bottom: 1px solid #e1e4e8;
`;

const Tab = styled.button`
  padding: 8px 16px;
  background: none;
  border: none;
  border-bottom: 2px solid ${props => props.$active ? '#0969da' : 'transparent'};
  color: ${props => props.$active ? '#0969da' : '#586069'};
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s;
  
  &:hover {
    color: #0969da;
  }
`;

const DropZone = styled.div`
  border: 2px dashed #d0d7de;
  border-radius: 6px;
  padding: 40px;
  text-align: center;
  cursor: pointer;
  transition: all 0.15s;
  background: ${props => props.$isDragging ? '#f6f8fa' : 'white'};
  border-color: ${props => props.$isDragging ? '#0969da' : '#d0d7de'};
  
  &:hover {
    border-color: #0969da;
    background: #f6f8fa;
  }
`;

const Input = styled.input`
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #d0d7de;
  border-radius: 6px;
  font-size: 14px;
  margin-bottom: 12px;
  box-sizing: border-box;
  
  &:focus {
    outline: none;
    border-color: #0969da;
  }
`;

const ErrorMessage = styled.div`
  padding: 12px;
  background: #fff1f0;
  border: 1px solid #ffccc7;
  border-radius: 6px;
  color: #cf222e;
  font-size: 13px;
  margin-top: 12px;
`;

const Footer = styled.div`
  padding: 16px 20px;
  border-top: 1px solid #e1e4e8;
  display: flex;
  justify-content: flex-end;
  gap: 12px;
`;

const Button = styled.button`
  padding: 8px 16px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s;
  border: 1px solid ${props => props.$primary ? '#0969da' : '#d0d7de'};
  background: ${props => props.$primary ? '#0969da' : 'white'};
  color: ${props => props.$primary ? 'white' : '#24292f'};
  
  &:hover {
    background: ${props => props.$primary ? '#0860ca' : '#f6f8fa'};
  }
`;

function ImportModal({ onImport, onClose }) {
  const [tab, setTab] = useState('file');
  const [url, setUrl] = useState('');
  const [error, setError] = useState('');
  const [isDragging, setIsDragging] = useState(false);

  const parseSpec = (content) => {
    try {
      const parsed = yaml.load(content);
      if (!parsed.openapi && !parsed.swagger) {
        throw new Error('Not a valid OpenAPI/Swagger spec');
      }
      return content;
    } catch (e) {
      try {
        const parsed = JSON.parse(content);
        if (!parsed.openapi && !parsed.swagger) {
          throw new Error('Not a valid OpenAPI/Swagger spec');
        }
        return yaml.dump(parsed, { indent: 2, lineWidth: -1 });
      } catch (e2) {
        throw new Error('Invalid YAML/JSON format');
      }
    }
  };

  const handleFile = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const spec = parseSpec(e.target.result);
        onImport(spec);
      } catch (err) {
        setError(err.message);
      }
    };
    reader.readAsText(file);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFile(file);
  };

  const handleUrlImport = async () => {
    setError('');
    try {
      const response = await fetch(url);
      const content = await response.text();
      const spec = parseSpec(content);
      onImport(spec);
    } catch (err) {
      setError('Failed to fetch from URL: ' + err.message);
    }
  };

  return (
    <Overlay onClick={onClose}>
      <Modal onClick={(e) => e.stopPropagation()}>
        <Header>
          <Title>📥 Import OpenAPI Spec</Title>
        </Header>
        <Content>
          <TabContainer>
            <Tab $active={tab === 'file'} onClick={() => setTab('file')}>File Upload</Tab>
            <Tab $active={tab === 'url'} onClick={() => setTab('url')}>From URL</Tab>
          </TabContainer>
          
          {tab === 'file' && (
            <DropZone
              $isDragging={isDragging}
              onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
              onDragLeave={() => setIsDragging(false)}
              onDrop={handleDrop}
              onClick={() => document.getElementById('fileInput').click()}
            >
              <div style={{ fontSize: '48px', marginBottom: '12px' }}>📁</div>
              <div style={{ fontSize: '14px', color: '#24292f', marginBottom: '4px' }}>
                Drop your OpenAPI file here or click to browse
              </div>
              <div style={{ fontSize: '12px', color: '#586069' }}>
                Supports YAML and JSON formats
              </div>
              <input
                id="fileInput"
                type="file"
                accept=".yaml,.yml,.json"
                style={{ display: 'none' }}
                onChange={(e) => e.target.files[0] && handleFile(e.target.files[0])}
              />
            </DropZone>
          )}
          
          {tab === 'url' && (
            <>
              <Input
                type="url"
                placeholder="https://api.example.com/openapi.yaml"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
              />
              <Button $primary onClick={handleUrlImport} style={{ width: '100%' }}>
                Import from URL
              </Button>
            </>
          )}
          
          {error && <ErrorMessage>{error}</ErrorMessage>}
        </Content>
        <Footer>
          <Button onClick={onClose}>Cancel</Button>
        </Footer>
      </Modal>
    </Overlay>
  );
}

export default ImportModal;
